import { api } from "@/api/api";
import { endpoints, EndpointType } from "@/constants/apiEndPoints";
import networkHelper from "@/composables/network-helper";

const { isSuccess } = networkHelper();

// Define types for parameters and return values
/* eslint-disable  @typescript-eslint/no-explicit-any */
export type Payload = Record<string, any>;
export type CustomParams = string[];
export type FormattingParams = Record<string, string>;
export type QueryParams = string;
export type Method = "get" | "post" | "put" | "delete" | "patch";
export type Headers = Record<string, string>;
export type ApiResponse = any; // Replace with your actual response type if available

interface Endpoint {
  url: string;
}

interface PaginatedPayload {
  limit?: number;
  page?: number;
  search?: string;
  customQuery?: QueryParams;
  customParams?: CustomParams;
  method?: Method;
  payload?: Payload;
  formattingParams?: FormattingParams;
}

interface NonPaginatedPayload {
  customQuery?: QueryParams;
  customParams?: CustomParams;
  method?: Method;
  search?: string;
  payload?: Payload;
}

type UnAuthenticatedCallArgs = {
  payload?: Payload | null;
  customQuery?: QueryParams | null;
  customParams?: CustomParams | null;
  method?: Method;
  endWithSlash?: boolean;
  formattingParams?: FormattingParams;
};

const actionsHandler = (endpointType: EndpointType) => {
  const endpoint: Endpoint = endpointType
    ? endpoints[endpointType]
    : ({} as Endpoint);

  const unAuthenticatedCall = ({
    payload = null,
    customQuery = null,
    customParams = null,
    method = "post",
    endWithSlash = false,
    formattingParams,
  }: UnAuthenticatedCallArgs) => {
    return async (): Promise<ApiResponse | false> => {
      let query = `${endpoint.url}`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      query = `${query}${
        customParams
          ? "/" + customParams.join("/") + (endWithSlash ? "/" : "")
          : ""
      }${customQuery ? "?" + customQuery : ""}`;

      const response = await api(query, payload, method, false, {});
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const authenticatedCall = ({
    payload = null,
    customQuery = null,
    customParams = null,
    method = "post",
    endWithSlash = false,
    formattingParams,
  }: UnAuthenticatedCallArgs) => {
    return async (): Promise<ApiResponse | false> => {
      let query = `${endpoint.url}`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      query = `${query}${
        customParams
          ? "/" + customParams.join("/") + (endWithSlash ? "/" : "")
          : ""
      }${customQuery ? "?" + customQuery : ""}`;

      const response = await api(query, payload, method);
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const getAllData = ({
    limit,
    page,
    search,
    customQuery = "",
    customParams,
    method = "get",
    payload = {},
    formattingParams,
  }: PaginatedPayload) => {
    return async (): Promise<ApiResponse | false> => {
      let query = `${endpoint.url}${
        customParams ? "/" + customParams.join("/") : ""
      }?limit=${limit}&page=${page}${search ? "&search=" + search : ""}${customQuery ? `&${customQuery}` : ""}`;
      if (formattingParams && Object.keys(formattingParams)?.length) {
        Object.keys(formattingParams).forEach((param) => {
          if (query.includes(`[${param}]`)) {
            query = query.replace(`[${param}]`, formattingParams[param]);
          }
        });
      }
      const blobType = customQuery?.includes("format");
      const response = await api(
        query,
        payload,
        method,
        false,
        {},
        false,
        {},
        blobType,
      );
      const { ok, data } = response;
      return { ok, data };
    };
  };

  const getAllDataWithoutPagination = ({
    customQuery = "",
    customParams,
    method = "get",
    payload = {},
    search,
  }: NonPaginatedPayload) => {
    return async (): Promise<ApiResponse | false> => {
      search = search ? "search=" + search : "";
      customQuery = customQuery && search ? "&" + customQuery : customQuery;
      const query = `
        ${endpoint.url}${customParams?.length ? "/" + customParams.join("/") : ""}${search || customQuery ? "?" + search + customQuery : ""}
      `;

      const response = await api(query, payload, method);
      response.ok = isSuccess(response?.status);
      return response;
    };
  };

  const getViewData = (
    id: string,
    customQuery?: QueryParams,
    headers: Headers = {},
    _logOutToken: string | null = null,
  ) => {
    return async (): Promise<ApiResponse | false> => {
      const query = `${endpoint.url}${id ? "/" + id : ""}${customQuery ? "?" + customQuery : ""}`;
      const response = await api(query, {}, "get", false, headers);
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  const createData = (
    payload: Payload,
    customQuery?: QueryParams,
    headers: Headers = {},
    manualEndpoint?: string,
    isCompleteManualEndPoint: boolean = false,
    isStream: boolean = false,
    _customOptions: Record<string, any> = {},
  ) => {
    return async (): Promise<ApiResponse | false> => {
      const query = `${
        manualEndpoint ? manualEndpoint : endpoint.url
      }${customQuery ? "?" + customQuery : ""}`;
      const response = await api(
        query,
        payload,
        "post",
        false,
        headers,
        isCompleteManualEndPoint,
      );
      if (isSuccess(response?.status)) {
        return isStream ? response : response?.data;
      } else {
        return false;
      }
    };
  };

  const updateData = (payload: Payload, customQuery?: QueryParams) => {
    return async (): Promise<ApiResponse | false> => {
      const query = `${endpoint.url}${payload.id ? "/" + payload.id : ""}${
        customQuery ? "?" + customQuery : ""
      }`;
      const response = await api(query, payload, "put");
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  const deleteData = (id: string) => {
    return async (): Promise<ApiResponse | false> => {
      const query = `${endpoint.url}/${id}`;
      const response = await api(query, {}, "delete");
      if (isSuccess(response?.status)) {
        return response?.data;
      } else {
        return false;
      }
    };
  };

  return {
    createData,
    getAllData,
    getAllDataWithoutPagination,
    getViewData,
    updateData,
    deleteData,
    unAuthenticatedCall,
    authenticatedCall,
  };
};

export default actionsHandler;

/* eslint-disable  @typescript-eslint/no-explicit-any */
